import { BiIconChat, BiIconMail, BiIconPhone } from '@bi-digital/icons-react';
import { BiButton, BiButtonGroup, BiTypography } from 'bi-web-components-react';
import { useTranslation } from 'react-i18next';
import { ComponentSelector } from '../../types';
import styles from './frontContent.module.css';
import useSWRImmutable from 'swr/immutable';
import { fetcher } from '@bino/utils/fetcher';
import { useState, useEffect } from 'react';
import { params } from '../../pages/contactUs';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare let embeddedservice_bootstrap: any;

export type FrontContentProps = {
    setComponent: (val: ComponentSelector) => void;
    mainTextHours1: string;
    mainTextHours2: string;
    mainTextChatHours: string;
    biaxChat?: boolean;
};

export const FrontContent = ({
    setComponent,
    mainTextHours1,
    mainTextHours2,
    mainTextChatHours,
    biaxChat,
}: FrontContentProps) => {
    const { t, i18n } = useTranslation('chat');

    const email = params.get('email') || '';
    const firstName = params.get('firstName') || '';
    const lastName = params.get('lastName') || '';

    const { data, isLoading } = useSWRImmutable('/api/chat/open', fetcher.get<{ chatOpen: boolean }>, {
        keepPreviousData: true,
    });

    const chatClosed = isLoading || !data;
    const chatText = isLoading ? t('loadingChat') : chatClosed ? t('chatClosed') : t('chatWithUs');

    const [waitingChat, setWaitingChat] = useState(false);

    useEffect(() => {
        const handler = () => {
            if (embeddedservice_bootstrap?.prechatAPI?.setVisiblePrechatFields) {
                embeddedservice_bootstrap.prechatAPI.setVisiblePrechatFields({
                    _firstName: {
                        value: firstName,
                        isEditableByEndUser: false,
                    },
                    _lastName: {
                        value: lastName,
                        isEditableByEndUser: false,
                    },
                    _email: {
                        value: email,
                        isEditableByEndUser: false,
                    },
                    CaseCategory: {
                        value: 'Admission',
                        isEditableByEndUser: false,
                    },
                });
            } else {
                console.error('PrechatAPI.setVisiblePrechatFields is not available');
            }
        };

        if (biaxChat) {
            window.addEventListener('onEmbeddedMessagingReady', handler);
        }

        return () => {
            window.removeEventListener('onEmbeddedMessagingReady', handler);
        };
    }, [email, firstName, lastName, biaxChat, i18n.language]);

    const openBiaxChat = () => {
        setWaitingChat(true);

        if (embeddedservice_bootstrap?.utilAPI?.launchChat) {
            embeddedservice_bootstrap.utilAPI
                .launchChat()
                .then(() => {
                    setWaitingChat(false);
                })
                .catch(() => {
                    setWaitingChat(false);
                    console.error('Error in chatHandler');
                });
        }
    };

    return (
        <div className={styles.frontContent}>
            <div className={styles.mainText}>
                <BiTypography fontStyle='bold'>
                    <p>{t('mainText')}</p>
                </BiTypography>
                <BiTypography>{`${t('mainTextDays1')} ${mainTextHours1}`}</BiTypography>
                <BiTypography>{mainTextChatHours}</BiTypography>
                <BiTypography>{`${t('mainTextDays2')} ${mainTextHours2}`}</BiTypography>
            </div>
            <div className={styles.mainButtons}>
                <BiButtonGroup direction='vertical' spacing='x-small'>
                    {biaxChat ? (
                        <BiButton
                            stretch
                            tabIndex={0}
                            disabled={chatClosed}
                            onClick={openBiaxChat}
                            submitting={waitingChat}
                            size={'medium'}
                        >
                            <BiIconChat slot='icon-left' aria-hidden='true' />
                            {chatText}
                        </BiButton>
                    ) : (
                        <BiButton
                            stretch
                            tabIndex={0}
                            disabled={chatClosed}
                            onClick={() => setComponent('chooseYourPath')}
                            size={'medium'}
                        >
                            <BiIconChat slot='icon-left' aria-hidden='true' />
                            {chatText}
                        </BiButton>
                    )}
                    <BiButton stretch onClick={() => setComponent('contactForm')} variant='filled' size={'medium'}>
                        <BiIconMail slot='icon-left' aria-hidden />
                        {t('contactForm')}
                    </BiButton>
                    <BiButton href='tel:+4746410000' target='_parent' stretch size={'medium'} variant='filled'>
                        <BiIconPhone slot='icon-left' aria-hidden />
                        {`${t('callUs')} 464 10 000`}
                    </BiButton>
                </BiButtonGroup>
            </div>
        </div>
    );
};
